import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

const contactInfo = [
  {
    icon: Phone,
    label: "Phone",
    value: "(251) 504-4452",
    href: "tel:251-504-4452",
  },
  {
    icon: Mail,
    label: "Email",
    value: "onyxproplumbing@gmail.com",
    href: "mailto:onyxproplumbing@gmail.com",
  },
  {
    icon: MapPin,
    label: "Address",
    value: "6642 AL-59 Unit D-3, Gulf Shores, AL 36542",
    href: "https://maps.google.com/?q=6642+AL-59+Unit+D-3+Gulf+Shores+AL+36542",
  },
  {
    icon: Clock,
    label: "Hours",
    value: "24/7 Emergency Service",
    href: null,
  },
];

export const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="py-24 bg-background relative overflow-hidden">
      {/* Background Accent */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/5 to-transparent pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10" ref={ref}>
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="text-primary font-semibold tracking-wider uppercase text-sm">
              Get In Touch
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-6 leading-tight">
              Let's Get It{" "}
              <span className="text-gradient-sky">Taken Care Of</span>
            </h2>
            <p className="text-muted-foreground text-lg mb-8">
              Whether it's an emergency or a planned project, we're here to help. Reach out anytime — we respond fast.
            </p>

            {/* Contact Cards */}
            <div className="space-y-3">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: -30 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.2 + index * 0.1 }}
                >
                  {item.href ? (
                    <a
                      href={item.href}
                      target={item.href.startsWith("http") ? "_blank" : undefined}
                      rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                      className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border hover:border-primary/50 transition-all duration-300 group"
                    >
                      <div className="p-3 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                        <item.icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground uppercase tracking-wider">{item.label}</div>
                        <div className="font-medium text-foreground group-hover:text-primary transition-colors">
                          {item.value}
                        </div>
                      </div>
                    </a>
                  ) : (
                    <div className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border">
                      <div className="p-3 rounded-lg bg-primary/10">
                        <item.icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground uppercase tracking-wider">{item.label}</div>
                        <div className="font-medium text-foreground">{item.value}</div>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* CTA Card */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-card rounded-2xl border border-border p-8 md:p-12 shadow-card"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
                <Phone className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Emergencies Don't Wait
              </h3>
              <p className="text-muted-foreground mb-8">
                Neither do we. Call now for immediate help — day or night, we'll be there.
              </p>
              <Button variant="hero" size="xl" className="w-full" asChild>
                <a href="tel:251-504-4452">
                  <Phone className="h-5 w-5" />
                  Call (251) 504-4452
                </a>
              </Button>
              <p className="text-sm text-muted-foreground mt-4">
                Available 24 hours a day, 7 days a week
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
